import { BaseTextField } from '@components/BaseTextField';
import { pushError, pushSuccess } from '@components/toast';
import { appConfig } from '@configs/index';
import {
    cameraPhotoController,
    cameraViewController,
    pcController,
    potentialGroupContraventionController,
    potentialPhotoContraventionController,
} from '@controllers/index';
import { IGetCameraPhotoProps } from '@EcamModel/controllers/ICameraPhotoHttpController';
import { IUpdateEvidencePhoto } from '@EcamModel/controllers/IGroupPotentialContraventionHttpController';
import { IUpdatePotentialContraventionPhoto } from '@EcamModel/controllers/IPotentialContraventionPhotoHttpController';
import {
    CameraPhotos,
    CameraPhotoVehicles,
    CameraServiceType,
    GroupPotentialContraventions,
    PhotoType,
} from '@EcamModel/model';
import { Box, CircularProgress, Grow, ListItemText, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import { useVerificationQueueMode } from '@pages/verification/hooks/VerificationProvider';
import color from '@theme/Colors';
import _, { orderBy } from 'lodash';
import moment, { Moment } from 'moment';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiInfo, FiSearch } from 'react-icons/fi';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { getSecond } from 'src/helpers';
import { useBackdrop } from 'src/providers/BackdropProvider';
import { PCInfo } from '..';
import { usePotentialContravention } from '../hooks/PotentialContraventionProvider';
import ConfirmEvidenceChangeDialog from './ConfirmEvidenceChangeDialog';
import { pcImage, PhotoItem } from './EvidencePhotoGroup';
import LivePhotoPreviewDialog from './LivePhotoPreviewDialog';
import LivePhotoThumbnail, { EvidenceTag } from './LivePhotoThumbnail';
import ScrollNavButtons from './ScrollNavButtons';
import useHorizontalScroll from './useHorizontalScroll';
import { buildPcVehicleContext } from '../utils/PcVehicleBbox';
import { photoTypeColorMap } from '@pages/manual-create-pcn/configs';
import { invalidateImageCache } from '@pages/cameras/live-photos/components/PreviewImage';
import { CameraView } from '@EcamModel/model/CameraView';
import useStyledAutocomplete from '@components/styled-autocomplete/useStyledAutocomplete';
import { ListProps } from '@Core';

type Props = {
    pc: PCInfo;
    photos: Partial<PhotoItem>[];
    prevGeoZones?: { x: number; y: number; geoZoneId: number | undefined }[][];
    geoZoneDisplayWidth: number;
    onEvidenceChanged?: () => void;
};

export type LivePhotosTimelineHandle = {
    scrollToPhotoId: (cameraPhotoId: number) => void;
    openPreviewAtPhotoId: (vehicleId: number) => boolean;
};

const photoTypeTagMap: Record<number, { label: string; color: string }> = {
    [PhotoType.Entry]: { label: 'Entry', color: photoTypeColorMap[PhotoType.Entry].main },
    [PhotoType.Validation]: { label: 'Validation', color: photoTypeColorMap[PhotoType.Validation].main },
    [PhotoType.Exit]: { label: 'Exit', color: photoTypeColorMap[PhotoType.Exit].main },
    [PhotoType.UnobstructedPlate]: { label: 'Full plate', color: photoTypeColorMap[PhotoType.UnobstructedPlate].main },
    [PhotoType.Overview]: { label: 'Overview', color: photoTypeColorMap[PhotoType.Overview].main },
};

// ─── main component ──────────────────────────────────────────────────────────
const LivePhotosTimeline = forwardRef<LivePhotosTimelineHandle, Props>(function LivePhotosTimeline(
    { pc, photos, prevGeoZones, geoZoneDisplayWidth, onEvidenceChanged },
    ref
) {
    const [livePhotos, setLivePhotos] = useState<CameraPhotos[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadingPrev, setLoadingPrev] = useState(false);
    const [noNewItemsChip, setNoNewItemsChip] = useState<{
        type: 'duplicate' | 'error' | 'boundary';
        range: string;
    } | null>(null);

    const noNewItemsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const showNoNewItemsChip = (type: 'duplicate' | 'error' | 'boundary', range: string = '') => {
        if (noNewItemsTimeoutRef.current) {
            clearTimeout(noNewItemsTimeoutRef.current);
        }
        setNoNewItemsChip({ type, range });
        noNewItemsTimeoutRef.current = setTimeout(() => {
            setNoNewItemsChip(null);
        }, 3000);
    };

    const [previewIndex, setPreviewIndex] = useState(-1);
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const pageRef = useRef(1);
    const totalRef = useRef(0);
    const extraMinutesRef = useRef(0);
    const prevExtraMinutesRef = useRef(0);
    const PAGE_SIZE = 100;

    const { scrollRef, scrollState, updateScrollState, handleScroll } = useHorizontalScroll(500);
    const [scrollLeft, setScrollLeft] = useState(0);

    const backdrop = useBackdrop();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { verificationQueueMode } = useVerificationQueueMode();
    const { setGroupPc, selectedPc, setSelectedPc, handleChangePlate, conditionAudit } = usePotentialContravention();

    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
        item: CameraPhotos;
    } | null>(null);
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        photoType: PhotoType;
        selectedItem: CameraPhotos | null;
        selectedVehicle: CameraPhotoVehicles | null; // which vehicle in the photo was picked
        currentEvidence: CameraPhotoVehicles | undefined;
    }>({
        open: false,
        photoType: PhotoType.Entry,
        selectedItem: null,
        selectedVehicle: null,
        currentEvidence: undefined,
    });
    const [plateInput, setPlateInput] = useState('');
    const [saving, setSaving] = useState(false);

    const handleRightClick = (e: React.MouseEvent, item: CameraPhotos) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, item });
    };

    const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

    const closeContextMenu = () => setContextMenu(null);

    const getCurrentEvidence = (photoType: PhotoType): CameraPhotoVehicles | undefined => {
        const current = pcImage(selectedPc || pc);
        switch (photoType) {
            case PhotoType.Entry:
                return current.entryPhoto;
            case PhotoType.Validation:
                return current.validationPhoto;
            case PhotoType.Exit:
                return current.exitPhoto;
            case PhotoType.UnobstructedPlate:
                return current.unobstructedPlatePhoto;
            default:
                return undefined;
        }
    };

    const canSelectAsType = (photoType: PhotoType, item: CameraPhotos): boolean => {
        if (photoType === PhotoType.UnobstructedPlate) return true;
        const captureAt = new Date(item.CaptureAt!);
        if (isNaN(captureAt.getTime())) return false;
        const current = pcImage(selectedPc || pc);
        const entryCaptureAt = current.entryPhoto?.CameraPhoto?.CaptureAt
            ? new Date(current.entryPhoto.CameraPhoto.CaptureAt)
            : null;
        const validationCaptureAt = current.validationPhoto?.CameraPhoto?.CaptureAt
            ? new Date(current.validationPhoto.CameraPhoto.CaptureAt)
            : null;
        const exitCaptureAt = current.exitPhoto?.CameraPhoto?.CaptureAt
            ? new Date(current.exitPhoto.CameraPhoto.CaptureAt)
            : null;
        if (photoType === PhotoType.Entry) {
            if (validationCaptureAt && validationCaptureAt.getTime() <= captureAt.getTime()) return false;
            if (exitCaptureAt && exitCaptureAt.getTime() <= captureAt.getTime()) return false;
        } else if (photoType === PhotoType.Validation) {
            if (entryCaptureAt && captureAt.getTime() <= entryCaptureAt.getTime()) return false;
            if (exitCaptureAt && captureAt.getTime() > exitCaptureAt.getTime()) return false;
        } else if (photoType === PhotoType.Exit) {
            if (entryCaptureAt && captureAt.getTime() <= entryCaptureAt.getTime()) return false;
            if (validationCaptureAt && captureAt.getTime() < validationCaptureAt.getTime()) return false;
        }
        return true;
    };

    const validateTime = (photoType: PhotoType, selectedItem: CameraPhotos): boolean => {
        const current = pcImage(selectedPc || pc);
        const selectedCaptureAt = new Date(selectedItem.CaptureAt!);

        if (isNaN(selectedCaptureAt.getTime())) {
            pushError('Selected item CaptureAt is not a valid date');
            return false;
        }

        const entryCaptureAt = current.entryPhoto?.CameraPhoto?.CaptureAt
            ? new Date(current.entryPhoto.CameraPhoto.CaptureAt)
            : null;
        const validationCaptureAt = current.validationPhoto?.CameraPhoto?.CaptureAt
            ? new Date(current.validationPhoto.CameraPhoto.CaptureAt)
            : null;
        const exitCaptureAt = current.exitPhoto?.CameraPhoto?.CaptureAt
            ? new Date(current.exitPhoto.CameraPhoto.CaptureAt)
            : null;

        if (photoType === PhotoType.Entry) {
            if (validationCaptureAt && validationCaptureAt.getTime() <= selectedCaptureAt.getTime()) {
                pushError('Entry time must be before Contravention validation time');
                return false;
            }
            if (exitCaptureAt && exitCaptureAt.getTime() <= selectedCaptureAt.getTime()) {
                pushError('Entry time must be before Exit time');
                return false;
            }
        } else if (photoType === PhotoType.Validation) {
            if (entryCaptureAt && selectedCaptureAt.getTime() <= entryCaptureAt.getTime()) {
                pushError('Contravention validation/Exit time must be after Entry time');
                return false;
            }
        } else if (photoType === PhotoType.Exit) {
            if (entryCaptureAt && selectedCaptureAt.getTime() <= entryCaptureAt.getTime()) {
                pushError('Contravention validation/Exit time must be after Entry time');
                return false;
            }
            if (validationCaptureAt && selectedCaptureAt.getTime() < validationCaptureAt.getTime()) {
                pushError('Exit time must be after Contravention validation time');
                return false;
            }
        }
        return true;
    };

    const handleSelectAsEvidence = (photoType: PhotoType, overrideItem?: CameraPhotos) => {
        const item = overrideItem || contextMenu?.item;
        if (!item) return;
        closeContextMenu();

        if (photoType !== PhotoType.UnobstructedPlate) {
            if (!validateTime(photoType, item)) return;
        }

        const currentEvidence = getCurrentEvidence(photoType);

        const vehicles = item.CameraPhotoVehicles ?? [];
        const autoVehicle = vehicles.length === 1 ? vehicles[0] : null;

        if (photoType === PhotoType.UnobstructedPlate) {
            setPlateInput(autoVehicle?.Plate || '');
        }

        setConfirmDialog({ open: true, photoType, selectedItem: item, selectedVehicle: autoVehicle, currentEvidence });
    };

    const handleConfirmEvidenceChange = async () => {
        const { photoType, selectedItem, selectedVehicle } = confirmDialog;
        if (!selectedItem || !selectedVehicle) return;

        setSaving(true);
        backdrop.setTrue();

        try {
            const pcId = (selectedPc || pc).Id!;
            const groupPcId = (selectedPc || pc).GroupPotentialContraventionId!;

            const photoPayload = {
                CameraPhotoVehicleId: selectedVehicle.Id!,
                PotentialContraventionId: pcId,
                PhotoType: photoType,
            };

            if (photoType === PhotoType.UnobstructedPlate && plateInput) {
                let vrnDetail = { Vrn: plateInput, Make: '', Model: '', Color: '', FuelType: '' };
                try {
                    const lookupRes = await pcController.lookUp(plateInput, {
                        GroupPotentialContraventionId: groupPcId,
                    });
                    const hasData = lookupRes.Make || lookupRes.Model || lookupRes.Color || lookupRes.FuelType;
                    if (hasData) {
                        vrnDetail = {
                            Vrn: plateInput,
                            Make: lookupRes.Make ?? '',
                            Model: lookupRes.Model ?? '',
                            Color: lookupRes.Color ?? '',
                            FuelType: lookupRes.FuelType ?? '',
                        };
                    }
                } catch {
                    // lookup failed, proceed with empty vehicle details
                }
                await handleChangePlate(vrnDetail, false);
            }

            const updateEvidencePhoto: IUpdateEvidencePhoto = {
                PotentialContraventionId: pcId,
                ...(photoType === PhotoType.Entry && { EntryPhotos: photoPayload }),
                ...(photoType === PhotoType.Validation && { ValidationPhotos: photoPayload }),
                ...(photoType === PhotoType.Exit && { ExitPhotos: photoPayload }),
                ...(photoType === PhotoType.UnobstructedPlate && { UnobstructedPlatePhotos: photoPayload }),
                IsVerificationAudit: conditionAudit,
            };

            await potentialGroupContraventionController.doUpdateEvidencePhoto(updateEvidencePhoto);
            queryClient.invalidateQueries(['trailAuditEventLogPCN']);

            const res = await potentialGroupContraventionController.getGroupPotentialContravention(groupPcId);
            const _res: GroupPotentialContraventions = {
                ...res,
                PotentialContraventions: orderBy(res.PotentialContraventions, (pc) => -pc.Step),
            };
            setGroupPc(_res);
            const currentPcId = (selectedPc || pc).Id;
            const updatedPc = _res.PotentialContraventions?.find((p) => p.Id === currentPcId);
            if (updatedPc) {
                setSelectedPc(updatedPc as PCInfo);
            }
            invalidateImageCache();
            pushSuccess('Evidence photo updated successfully');
            onEvidenceChanged?.();
        } catch (err) {
            pushError('Failed to update evidence photo');
        } finally {
            setSaving(false);
            backdrop.setFalse();
            setConfirmDialog({
                open: false,
                photoType: PhotoType.Entry,
                selectedItem: null,
                selectedVehicle: null,
                currentEvidence: undefined,
            });
        }
    };

    const ITEM_W = 340;
    const GAP = 8;
    const SLOT = ITEM_W + GAP;

    const scrollToIndexAndHighlight = useCallback(
        (index: number) => {
            setHighlightIndex(index);
            setTimeout(() => setHighlightIndex(-1), 2300);
            const el = scrollRef.current;
            if (el) {
                const itemLeft = index * SLOT;
                const target = itemLeft - el.clientWidth / 2 + ITEM_W / 2;
                el.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
            }
        },
        [scrollRef, SLOT, ITEM_W]
    );

    useImperativeHandle(
        ref,
        () => ({
            scrollToPhotoId: (cameraPhotoId: number) => {
                const idx = livePhotos.findIndex((p) => p.Id === cameraPhotoId);
                if (idx >= 0) {
                    scrollToIndexAndHighlight(idx);
                    setFocusedIndex(idx);
                }
            },
            openPreviewAtPhotoId: (vehicleId: number): boolean => {
                const idx = livePhotos.findIndex((p) => p.CameraPhotoVehicles?.some((v) => v.Id === vehicleId));
                if (idx >= 0) {
                    setPreviewIndex(idx);
                    return true;
                }
                return false;
            },
        }),
        [livePhotos, scrollToIndexAndHighlight]
    );

    const evidenceTagMap = new Map<number, EvidenceTag[]>();
    photos.forEach((p) => {
        const id = p.photo?.CameraPhotoId;
        if (!id) return;
        const tag = p.photoType != null ? photoTypeTagMap[p.photoType] : undefined;
        if (!tag) return;
        const existing = evidenceTagMap.get(id) || [];
        existing.push(tag);
        evidenceTagMap.set(id, existing);
    });

    const photoItemByVehicleId = new Map<number, Partial<PhotoItem>>();
    photos.forEach((p) => {
        const id = p.photo?.Id;
        if (id != null) photoItemByVehicleId.set(id, p);
    });

    const evidenceVehicleIds = new Set<number>(photoItemByVehicleId.keys());

    // `photos` is rebuilt every parent render, so memoize on the id list rather than the Set itself.
    const currentPc = selectedPc || pc;
    const evidenceVehicleIdsKey = Array.from(evidenceVehicleIds).sort((a, b) => a - b).join(',');
    const vehicleContext = useMemo(
        () =>
            buildPcVehicleContext(
                currentPc,
                new Set(evidenceVehicleIdsKey ? evidenceVehicleIdsKey.split(',').map(Number) : [])
            ),
        [currentPc, evidenceVehicleIdsKey]
    );

    const handleSaveAdjustment = async (dataUrl: string, item: CameraPhotos) => {
        const photoItem = (item.CameraPhotoVehicles ?? [])
            .map((v) => (v.Id != null ? photoItemByVehicleId.get(v.Id) : undefined))
            .find(Boolean);

        if (!photoItem?.photoVehicleId || photoItem.photoType == null) return;

        const photoOptions: IUpdatePotentialContraventionPhoto = {
            photoVehicleId: photoItem.photoVehicleId,
            imageBase64: dataUrl,
            photoType: photoItem.photoType,
        };

        backdrop.setTrue();
        try {
            await potentialPhotoContraventionController.updatePhoto(photoOptions);
            queryClient.invalidateQueries(['trailAuditEventLogPCN']);
            queryClient.invalidateQueries(['livePhotos']);

            const groupPcId = (selectedPc || pc).GroupPotentialContraventionId!;
            const res = await potentialGroupContraventionController.getGroupPotentialContravention(groupPcId);
            const _res: GroupPotentialContraventions = {
                ...res,
                PotentialContraventions: orderBy(res.PotentialContraventions, (p) => -p.Step),
            };
            setGroupPc(_res);

            const currentPcId = (selectedPc || pc).Id;
            const updatedPc = _res.PotentialContraventions?.find((p) => p.Id === currentPcId);
            if (updatedPc) {
                setSelectedPc(updatedPc as PCInfo);
            }
            invalidateImageCache();
            pushSuccess('Saved successfully');
        } finally {
            backdrop.setFalse();
        }
    };

    // ─── filter state ────────────────────────────────────────────────────
    const [filterPlate, setFilterPlate] = useState<string | undefined>(undefined);
    const [inputPlate, setInputPlate] = useState<string | undefined>(undefined);
    const [dayStartTime, setDayStartTime] = useState<Moment | null>(null);
    const [dayEndTime, setDayEndTime] = useState<Moment | null>(null);
    const [cameraViews, setCameraViews] = useState<CameraView[]>([]);
    const [selectedCameraView, setSelectedCameraView] = useState<CameraView | null>(null);

    const debouncedSetPlate = useRef(_.debounce((val?: string) => setFilterPlate(val), 500)).current;

    const handleChangeTime = (from: Moment | null, to: Moment | null) => {
        let updatedFrom = from;
        let updatedTo = to;
        if (!updatedTo) updatedTo = moment().endOf('day');
        if (updatedFrom && updatedTo && updatedFrom.isAfter(updatedTo)) {
            [updatedFrom, updatedTo] = [updatedTo, updatedFrom];
        }
        setDayStartTime(updatedFrom);
        setDayEndTime(updatedTo);
    };

    const buildFilter = useCallback(
        (page: number): IGetCameraPhotoProps => {
            const tz = pc.Timezone || appConfig.timeZone;
            const BASE_MINUTES = 3;
            const extra = extraMinutesRef.current;
            const prevExtra = prevExtraMinutesRef.current;
            const baseFrom = moment(pc.EntryAt).tz(tz);
            const baseTo = moment(pc.ExitAt).tz(tz);

            const dayStart = baseFrom.clone().startOf('day');
            const fromDate = dayStartTime
                ? dayStart
                : moment.max([baseFrom.clone().subtract(BASE_MINUTES + prevExtra, 'minutes'), dayStart]);

            const dayEnd = baseTo.clone().endOf('day');
            const toDate = dayEndTime
                ? dayEnd
                : moment.min([baseTo.clone().add(BASE_MINUTES + extra, 'minutes'), dayEnd]);

            const viewId = selectedPc?.EntryPhotos?.length
                ? selectedPc.EntryPhotos.reduce((max, item) => ((item.Id ?? 0) > (max.Id ?? 0) ? item : max))
                      ?.CameraPhoto?.ViewId
                : undefined;

            return {
                page,
                pageSize: PAGE_SIZE,
                sorts: ['-CaptureAt'],
                CameraId: pc.CameraId ?? 0,
                FromDate: fromDate.toDate(),
                ToDate: toDate.toDate(),
                FromTime: dayStartTime ? Math.max(0, getSecond(dayStartTime) - extra * 60) : undefined,
                ToTime: dayEndTime ? Math.min(86400, getSecond(dayEndTime) + extra * 60) : undefined,
                Plate: filterPlate,
                filter: !_.isNil(viewId) ? { ViewId: viewId } : undefined,
                HasAiPlateCandidate: true,
                // filter: selectedCameraView ? { ViewId: [selectedCameraView.Id] } : undefined,
                // ViewId: selectedCameraView ? selectedCameraView.Id : undefined,
            };
        },
        [pc.CameraId, pc.EntryAt, pc.Timezone, filterPlate, dayStartTime, dayEndTime]
    );

    const livePhotosQueryKey = [
        'livePhotos',
        pc.CameraId,
        pc.EntryAt,
        pc.Timezone,
        filterPlate,
        dayStartTime?.valueOf(),
        dayEndTime?.valueOf(),
    ];

    const livePhotosQuery = useQuery(livePhotosQueryKey, () => cameraPhotoController.getLivePhotos(buildFilter(1)), {
        enabled: !!pc.CameraId,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const isViewFilterEnabled = (selectedPc || pc)?.Camera?.ServiceType === CameraServiceType.PTZ;

    useEffect(() => {
        const fetchCameraViews = async () => {
            try {
                const response = await cameraViewController.list({
                    page: 1,
                    pageSize: 10000,
                    filter: { CameraId: pc.CameraId },
                });
                setCameraViews(response.rows || []);
            } catch {
                setCameraViews([]);
            }
        };
        if (pc.CameraId && isViewFilterEnabled) {
            // fetchCameraViews();
        } else {
            setCameraViews([]);
            setSelectedCameraView(null);
        }
    }, [pc.CameraId, isViewFilterEnabled]);

    const cameraViewAutocomplete = useStyledAutocomplete<CameraView, ListProps<CameraView>>({
        list: {
            options: cameraViews,
            isFiltered(option, searchText) {
                return (option.CameraViewName ?? '').toLowerCase().includes(searchText.toLowerCase());
            },
        },
        dependencyList: [cameraViews],
    });

    useEffect(() => {
        setLoading(livePhotosQuery.isLoading);
    }, [livePhotosQuery.isLoading]);

    useEffect(() => {
        if (!livePhotosQuery.data) return;
        const res = livePhotosQuery.data;
        pageRef.current = 1;
        extraMinutesRef.current = 0;
        prevExtraMinutesRef.current = 0;
        totalRef.current = res.total;
        setLivePhotos(res.rows);
        const el = scrollRef.current;
        if (el) el.scrollLeft = 0;
        setScrollLeft(0);
        updateScrollState();
        requestAnimationFrame(() => {
            if (scrollRef.current) {
                setScrollLeft(scrollRef.current.scrollLeft);
                updateScrollState();
            }
        });
    }, [livePhotosQuery.data]);

    const hasMore = livePhotos.length < totalRef.current;

    const loadMore = useCallback(async () => {
        if (loadingMore) return;

        if (!hasMore) {
            const tz = pc.Timezone || appConfig.timeZone;
            const baseTo = moment(pc.ExitAt).tz(tz);
            const dayEnd = baseTo.clone().endOf('day');

            const remainingToEnd = dayEnd.diff(baseTo.clone().add(extraMinutesRef.current, 'minutes'), 'minutes');
            const stepMinutes = remainingToEnd < 20 ? Math.max(remainingToEnd, 0) : 20;

            if (stepMinutes <= 0) {
                pushError('No more photos available for this day');
                return;
            }

            extraMinutesRef.current += stepMinutes;
            setLoadingMore(true);
            try {
                const filter = buildFilter(1);
                const fromDate = moment(filter.FromDate).tz(pc.Timezone || appConfig.timeZone);
                const toDate = moment(filter.ToDate).tz(pc.Timezone || appConfig.timeZone);
                const res = await cameraPhotoController.getLivePhotos(filter);
                pageRef.current = 1;
                totalRef.current = res.total;
                setLivePhotos((prev) => {
                    const existingIds = new Set(prev.map((p) => p.Id).filter(Boolean));
                    const newItems = res.rows.filter((p) => !existingIds.has(p.Id));
                    if (newItems.length === 0) {
                        showNoNewItemsChip('duplicate', `${fromDate.format('h:mm A')} – ${toDate.format('h:mm A')}`);
                    } else {
                        setNoNewItemsChip(null);
                    }
                    return newItems.length > 0 ? [...prev, ...newItems] : prev;
                });
            } catch {
                showNoNewItemsChip('error', '');
            } finally {
                setLoadingMore(false);
            }
            return;
        }

        setLoadingMore(true);
        try {
            const nextPage = pageRef.current + 1;
            const res = await cameraPhotoController.getLivePhotos(buildFilter(nextPage));
            pageRef.current = nextPage;
            totalRef.current = res.total;
            setLivePhotos((prev) => [...prev, ...res.rows]);
            setNoNewItemsChip(null);
        } catch {
        } finally {
            setLoadingMore(false);
        }
    }, [loadingMore, hasMore, buildFilter]);

    const loadPrev = useCallback(async (): Promise<number> => {
        if (loadingMore || loadingPrev) return 0;
        const tz = pc.Timezone || appConfig.timeZone;
        const baseFrom = moment(pc.EntryAt).tz(tz);
        const dayStart = baseFrom.clone().startOf('day');

        const remainingFromStart = baseFrom
            .clone()
            .subtract(prevExtraMinutesRef.current, 'minutes')
            .diff(dayStart, 'minutes');
        const stepMinutes = remainingFromStart < 20 ? Math.max(remainingFromStart, 0) : 20;

        if (stepMinutes <= 0) {
            pushError('No more photos available for this day');
            return 0;
        }

        prevExtraMinutesRef.current += stepMinutes;
        setLoadingPrev(true);
        try {
            const filter = buildFilter(1);
            const tzStr = pc.Timezone || appConfig.timeZone;
            const fromDate = moment(filter.FromDate).tz(tzStr);
            const toDate = moment(filter.ToDate).tz(tzStr);
            const res = await cameraPhotoController.getLivePhotos(filter);
            let prependCount = 0;
            setLivePhotos((prev) => {
                const existingIds = new Set(prev.map((p) => p.Id).filter(Boolean));
                const newItems = res.rows.filter((p) => !existingIds.has(p.Id));
                prependCount = newItems.length;
                if (prependCount === 0) {
                    showNoNewItemsChip('duplicate', `${fromDate.format('h:mm A')} – ${toDate.format('h:mm A')}`);
                } else {
                    setNoNewItemsChip(null);
                }
                return newItems.length > 0 ? [...newItems, ...prev] : prev;
            });
            totalRef.current = res.total;
            return prependCount;
        } catch {
            showNoNewItemsChip('error', '');
            return 0;
        } finally {
            setLoadingPrev(false);
        }
    }, [loadingMore, loadingPrev, buildFilter]);

    const handleContainerScroll = useCallback(() => {
        updateScrollState();
        const el = scrollRef.current;
        if (!el) return;
        setScrollLeft(el.scrollLeft);
        if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 100) {
            loadMore();
        }
    }, [updateScrollState, loadMore]);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

            if (contextMenu !== null) {
                const key = e.key.toLowerCase();
                if (key === 'p') {
                    e.preventDefault();
                    e.stopImmediatePropagation?.();
                    const item = contextMenu.item;
                    const idx = livePhotos.findIndex((p) => p.Id === item.Id);
                    if (idx >= 0) setPreviewIndex(idx);
                    closeContextMenu();
                    return;
                }

                const kbdItem = contextMenu.item;
                const kbdTags = kbdItem.Id != null ? evidenceTagMap.get(kbdItem.Id) : undefined;
                const isAlreadyFullPlate = kbdTags?.some((t) => t.label === 'Full plate');

                if (
                    key === '1' &&
                    canSelectAsType(PhotoType.Entry, kbdItem) &&
                    !kbdTags?.some((t) => t.label === 'Entry')
                ) {
                    e.preventDefault();
                    e.stopImmediatePropagation?.();
                    handleSelectAsEvidence(PhotoType.Entry);
                    return;
                }
                if (
                    key === '2' &&
                    canSelectAsType(PhotoType.Validation, kbdItem) &&
                    !kbdTags?.some((t) => t.label === 'Validation')
                ) {
                    e.preventDefault();
                    e.stopImmediatePropagation?.();
                    handleSelectAsEvidence(PhotoType.Validation);
                    return;
                }
                if (
                    key === '3' &&
                    canSelectAsType(PhotoType.Exit, kbdItem) &&
                    !kbdTags?.some((t) => t.label === 'Exit')
                ) {
                    e.preventDefault();
                    e.stopImmediatePropagation?.();
                    handleSelectAsEvidence(PhotoType.Exit);
                    return;
                }
                if (!isAlreadyFullPlate) {
                    if (key === '4') {
                        e.preventDefault();
                        e.stopImmediatePropagation?.();
                        handleSelectAsEvidence(PhotoType.UnobstructedPlate);
                        return;
                    }
                }
                return;
            }

            if (previewIndex >= 0 || confirmDialog.open) return;
            if (document.querySelector('.MuiDialog-root, .MuiModal-root')) return;

            const el = scrollRef.current;
            if (!el) return;

            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                e.stopImmediatePropagation();

                if (e.key === 'ArrowRight') {
                    const next = Math.min(focusedIndex + 1, livePhotos.length - 1);
                    const itemLeft = next * SLOT;
                    const target = itemLeft - el.clientWidth / 2 + ITEM_W / 2;
                    el.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
                    setFocusedIndex(next);
                    // At the last item, load more so the next ArrowRight can advance
                    if (focusedIndex >= livePhotos.length - 1) {
                        loadMore();
                    }
                } else {
                    if (focusedIndex <= 0) {
                        // At the first item, load previous photos
                        loadPrev().then((prependCount) => {
                            if (prependCount > 0) {
                                // Shift focus to the same photo (now offset by prepended items)
                                const newIndex = prependCount;
                                setFocusedIndex(newIndex);
                                const itemLeft = newIndex * SLOT;
                                const target = itemLeft - el.clientWidth / 2 + ITEM_W / 2;
                                el.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
                            }
                        });
                    } else {
                        const next = Math.max(focusedIndex - 1, 0);
                        const itemLeft = next * SLOT;
                        const target = itemLeft - el.clientWidth / 2 + ITEM_W / 2;
                        el.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
                        setFocusedIndex(next);
                    }
                }
                return;
            }

            // ── Space to open context menu on focused item ──
            if (e.key === ' ' || e.code === 'Space') {
                e.preventDefault();
                if (livePhotos.length === 0) return;
                const item = livePhotos[focusedIndex];
                if (!item) return;
                const domEl = itemRefs.current.get(focusedIndex);
                if (domEl) {
                    const rect = domEl.getBoundingClientRect();
                    setContextMenu({ mouseX: rect.left + rect.width / 2, mouseY: rect.top, item });
                } else {
                    const elRect = el.getBoundingClientRect();
                    const approxX = elRect.left + (focusedIndex * SLOT - el.scrollLeft) + ITEM_W / 2;
                    const approxY = elRect.top;
                    setContextMenu({ mouseX: approxX, mouseY: approxY, item });
                }
                return;
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [
        previewIndex,
        confirmDialog.open,
        livePhotos,
        focusedIndex,
        SLOT,
        ITEM_W,
        scrollRef,
        contextMenu,
        loadMore,
        loadPrev,
    ]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            const isScrollingRight = e.deltaY > 0;

            handleScroll(isScrollingRight ? 'right' : 'left');

            if (isScrollingRight && el.scrollLeft + el.clientWidth >= el.scrollWidth - 100) {
                loadMore();
            }

            if (!isScrollingRight && el.scrollLeft <= 100) {
                loadPrev();
            }
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [handleScroll, loadMore, loadPrev, loading, livePhotos.length]);

    return (
        <Stack mt={2} spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" px={0.5} gap={2}>
                <Typography color="#555" sx={{ whiteSpace: 'nowrap' }}>
                    Live photos timeline ({totalRef?.current ?? 0})
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <BaseTextField
                        placeholder="E.g., ABC**, A**12"
                        value={inputPlate}
                        onChange={(e) => {
                            setInputPlate(e.target.value);
                            debouncedSetPlate(e.target.value);
                        }}
                        maxLength={10}
                        size="small"
                        sx={{
                            width: 200,
                            flexShrink: 0,
                            '& .MuiInputBase-root': { height: 30, fontSize: 12 },
                        }}
                        InputProps={{
                            endAdornment: (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <FiSearch size={12} style={{ color: 'rgba(156,154,154,0.38)' }} />
                                    <Tooltip
                                        title="Use * as a wildcard. Example: 'ABC**' or 'A**12'"
                                        placement="top"
                                        arrow
                                    >
                                        <span style={{ height: 12 }}>
                                            <FiInfo size={10} style={{ cursor: 'pointer' }} />
                                        </span>
                                    </Tooltip>
                                </Stack>
                            ),
                        }}
                    />

                    {/* {isViewFilterEnabled && cameraViews.length > 0 && (
                        <Stack
                            sx={{
                                width: 200,
                                flexShrink: 0,
                                '& .MuiInputBase-root': { height: 30, fontSize: 12 },
                                '& .MuiInputLabel-root': { fontSize: 12 },
                            }}
                        >
                            <StyledAutocomplete
                                {...cameraViewAutocomplete}
                                getOptionLabel={(o) => o.CameraViewName ?? ''}
                                label="Camera view"
                                value={selectedCameraView ?? undefined}
                                placeholder="Select view"
                                onChange={(c) => setSelectedCameraView(c ?? null)}
                                wrapperWidth="100%"
                            />
                        </Stack>
                    )} */}

                    <TimePicker
                        label="From"
                        value={dayStartTime}
                        ampm={false}
                        onAccept={(v) => handleChangeTime(v, dayEndTime)}
                        sx={{
                            width: 200,
                            flexShrink: 0,
                            '& .MuiSvgIcon-root': { width: 14, height: 14 },
                            '& .MuiInputBase-root': { height: 30, fontSize: 12 },
                            '& .MuiInputLabel-root': { fontSize: 12 },
                        }}
                        slotProps={{ textField: { size: 'small' } }}
                    />
                    <TimePicker
                        label="To"
                        ampm={false}
                        disabled={!dayStartTime}
                        value={dayEndTime}
                        onAccept={(v) => handleChangeTime(dayStartTime, v)}
                        sx={{
                            width: 200,
                            flexShrink: 0,
                            '& .MuiSvgIcon-root': { width: 14, height: 14 },
                            '& .MuiInputBase-root': { height: 30, fontSize: 12 },
                            '& .MuiInputLabel-root': { fontSize: 12 },
                        }}
                        slotProps={{ textField: { size: 'small' } }}
                    />
                </Stack>
            </Stack>

            {loading || !livePhotos.length ? (
                <Stack
                    direction="row"
                    justifyContent="center"
                    py={10}
                    bgcolor={color.grey100}
                    borderRadius={1}
                    width={'100%'}
                >
                    {loading ? (
                        <Grow in timeout={400}>
                            <CircularProgress size={20} />
                        </Grow>
                    ) : (
                        <Typography variant="body2" color="#999" px={0.5}>
                            No live photos found
                        </Typography>
                    )}
                </Stack>
            ) : (
                <Box sx={{ position: 'relative' }}>
                    <AnimatePresence>
                        {noNewItemsChip && (
                            <motion.div
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.2 }}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    zIndex: 20,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    pointerEvents: noNewItemsChip.type === 'error' ? 'auto' : 'none',
                                }}
                                onClick={
                                    noNewItemsChip.type === 'error'
                                        ? () => {
                                              setNoNewItemsChip(null);
                                              loadMore();
                                          }
                                        : undefined
                                }
                            >
                                <div
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '4px 12px',
                                        backgroundColor: noNewItemsChip.type === 'error' ? '#fef3c7' : '#f3f4f6',
                                        borderRadius: 16,
                                        fontSize: 12,
                                        color: noNewItemsChip.type === 'error' ? '#92400e' : '#6b7280',
                                        border: `1px solid ${noNewItemsChip.type === 'error' ? '#fcd34d' : '#e5e7eb'}`,
                                    }}
                                >
                                    <span>
                                        {noNewItemsChip.type === 'duplicate'
                                            ? '🕐'
                                            : noNewItemsChip.type === 'error'
                                              ? '⚠️'
                                              : '⛔'}
                                    </span>
                                    <span>
                                        {noNewItemsChip.type === 'duplicate' &&
                                            `No new photos ${noNewItemsChip.range} — scroll for more`}
                                        {noNewItemsChip.type === 'error' && 'Unable to load photos — tap to retry'}
                                        {noNewItemsChip.type === 'boundary' && 'No more photos for this day'}
                                    </span>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <ScrollNavButtons
                        canScrollLeft={scrollState.canScrollLeft}
                        canScrollRight={scrollState.canScrollRight}
                        onScrollLeft={() => handleScroll('left')}
                        onScrollRight={() => handleScroll('right')}
                        fadeWidth={36}
                        buttonSize={26}
                        iconFontSize="small"
                        gradientOpacity={0.85}
                    />

                    {/* Scrollable container — virtualized */}
                    <Box
                        ref={scrollRef}
                        onScroll={handleContainerScroll}
                        sx={{
                            overflowX: 'auto',
                            scrollBehavior: 'smooth',
                            scrollbarWidth: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                            py: 0.5,
                            px: 0.5,
                            position: 'relative',
                        }}
                    >
                        {(() => {
                            const BUFFER = 5;
                            const totalW = livePhotos.length * SLOT - GAP;
                            const el = scrollRef.current;
                            const viewportW = el?.clientWidth ?? 800;
                            const sl = scrollLeft;

                            const startIdx = Math.max(0, Math.floor(sl / SLOT) - BUFFER);
                            const endIdx = Math.min(livePhotos.length - 1, Math.ceil((sl + viewportW) / SLOT) + BUFFER);

                            const visibleItems: React.ReactNode[] = [];
                            for (let i = startIdx; i <= endIdx; i++) {
                                const item = livePhotos[i];
                                visibleItems.push(
                                    <Box
                                        key={item.Id}
                                        ref={(el: HTMLDivElement | null) => {
                                            if (el) itemRefs.current.set(i, el);
                                            else itemRefs.current.delete(i);
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            left: i * SLOT + 4,
                                            top: 4,
                                            width: ITEM_W,
                                        }}
                                    >
                                        <LivePhotoThumbnail
                                            item={item}
                                            evidenceTags={item.Id != null ? evidenceTagMap.get(item.Id) || [] : []}
                                            timezone={pc.Timezone || appConfig.timeZone}
                                            prevGeoZones={prevGeoZones}
                                            geoZoneDisplayWidth={geoZoneDisplayWidth}
                                            vehicleContext={vehicleContext}
                                            highlighted={i === highlightIndex}
                                            focused={i === focusedIndex}
                                            onClick={() => {
                                                setFocusedIndex(i);
                                                setPreviewIndex(i);
                                            }}
                                            onContextMenu={(e) => {
                                                setFocusedIndex(i);
                                                handleRightClick(e, item);
                                            }}
                                        />
                                    </Box>
                                );
                            }

                            return (
                                <Box
                                    sx={{
                                        position: 'relative',
                                        height: ITEM_W * (9 / 16) + 8,
                                        width: totalW,
                                    }}
                                >
                                    {loadingPrev && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                left: 8,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                zIndex: 10,

                                                borderRadius: '50%',
                                                width: 44,
                                                height: 44,
                                                bgcolor: color.white,

                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',

                                                animation: 'pulseFadeIn 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards',

                                                '@keyframes pulseFadeIn': {
                                                    '0%': {
                                                        opacity: 0,
                                                        transform: 'translateY(-50%) scale(0.8)',
                                                    },
                                                    '100%': {
                                                        opacity: 1,
                                                        transform: 'translateY(-50%) scale(1)',
                                                    },
                                                },
                                            }}
                                        >
                                            <CircularProgress size={20} sx={{ color: 'primary.main' }} />
                                        </Box>
                                    )}
                                    {visibleItems}
                                    {loadingMore && (
                                        <Stack
                                            sx={{
                                                position: 'absolute',
                                                left: totalW + 12,
                                                top: 0,
                                                bottom: 0,
                                                width: 60,
                                            }}
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <CircularProgress size={18} />
                                        </Stack>
                                    )}
                                </Box>
                            );
                        })()}
                    </Box>
                </Box>
            )}

            {/* Preview dialog */}
            <LivePhotoPreviewDialog
                open={previewIndex >= 0}
                onClose={(lastIndex) => {
                    setPreviewIndex(-1);
                    setFocusedIndex(lastIndex);
                    const targetScrollLeft = Math.max(
                        0,
                        lastIndex * SLOT - (scrollRef.current?.clientWidth ?? 0) / 2 + ITEM_W / 2
                    );
                    setScrollLeft(targetScrollLeft);
                    requestAnimationFrame(() => {
                        setTimeout(() => {
                            scrollToIndexAndHighlight(lastIndex);
                        }, 50);
                    });
                }}
                items={livePhotos}
                initialIndex={previewIndex}
                hasMore={hasMore}
                onLoadMore={loadMore}
                onLoadPrev={loadPrev}
                timezone={pc.Timezone || appConfig.timeZone}
                prevGeoZones={prevGeoZones}
                geoZoneDisplayWidth={geoZoneDisplayWidth}
                evidenceTagMap={evidenceTagMap}
                vehicleContext={vehicleContext}
                canSelectAs={canSelectAsType}
                onSelectAsEvidence={(photoType, item) => handleSelectAsEvidence(photoType, item)}
                onSaveAdjustment={handleSaveAdjustment}
            />

            {/* Context menu for evidence selection */}
            <Menu
                open={contextMenu !== null}
                onClose={closeContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                autoFocus={false}
                disableAutoFocusItem
                slotProps={{
                    paper: { sx: { minWidth: 220, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } },
                }}
            >
                <MenuItem
                    onClick={() => {
                        if (!contextMenu) return;
                        const idx = livePhotos.findIndex((p) => p.Id === contextMenu.item.Id);
                        if (idx >= 0) setPreviewIndex(idx);
                        closeContextMenu();
                    }}
                >
                    <ListItemText>Preview Photo</ListItemText>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2, fontSize: 12 }}>
                        P
                    </Typography>
                </MenuItem>

                {(() => {
                    const menuItem = contextMenu?.item;
                    const itemId = menuItem?.Id;
                    const tags = itemId != null ? evidenceTagMap.get(itemId) : undefined;
                    const isAlreadyFullPlate = tags?.some((t) => t.label === 'Full plate');
                    const hasVehicle = menuItem?.CameraPhotoVehicles?.some((v) => v.Id != null) ?? false;

                    if (!hasVehicle || !menuItem) return null;

                    const showEntry =
                        canSelectAsType(PhotoType.Entry, menuItem) && !tags?.some((t) => t.label === 'Entry');
                    const showValidation =
                        canSelectAsType(PhotoType.Validation, menuItem) && !tags?.some((t) => t.label === 'Validation');
                    const showExit =
                        canSelectAsType(PhotoType.Exit, menuItem) && !tags?.some((t) => t.label === 'Exit');

                    return (
                        <>
                            {showEntry && (
                                <MenuItem onClick={() => handleSelectAsEvidence(PhotoType.Entry)}>
                                    <ListItemText>Select as Entry Photo</ListItemText>
                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2, fontSize: 12 }}>
                                        1
                                    </Typography>
                                </MenuItem>
                            )}
                            {showValidation && (
                                <MenuItem onClick={() => handleSelectAsEvidence(PhotoType.Validation)}>
                                    <ListItemText>Select as Validation Photo</ListItemText>
                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2, fontSize: 12 }}>
                                        2
                                    </Typography>
                                </MenuItem>
                            )}
                            {showExit && (
                                <MenuItem onClick={() => handleSelectAsEvidence(PhotoType.Exit)}>
                                    <ListItemText>Select as Exit Photo</ListItemText>
                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2, fontSize: 12 }}>
                                        3
                                    </Typography>
                                </MenuItem>
                            )}
                            {!isAlreadyFullPlate && (
                                <MenuItem onClick={() => handleSelectAsEvidence(PhotoType.UnobstructedPlate)}>
                                    <ListItemText>Select as Full Plate Photo</ListItemText>
                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2, fontSize: 12 }}>
                                        4
                                    </Typography>
                                </MenuItem>
                            )}
                        </>
                    );
                })()}
            </Menu>

            {/* Confirmation dialog */}
            <ConfirmEvidenceChangeDialog
                open={confirmDialog.open}
                onClose={() =>
                    setConfirmDialog({
                        open: false,
                        photoType: PhotoType.Entry,
                        selectedItem: null,
                        selectedVehicle: null,
                        currentEvidence: undefined,
                    })
                }
                onConfirm={handleConfirmEvidenceChange}
                photoType={confirmDialog.photoType}
                currentPhoto={confirmDialog.currentEvidence}
                newPhoto={confirmDialog.selectedItem}
                vehicles={confirmDialog.selectedItem?.CameraPhotoVehicles ?? []}
                selectedVehicle={confirmDialog.selectedVehicle}
                onVehicleSelect={(v) =>
                    setConfirmDialog((prev) => {
                        // Sync plateInput when vehicle is selected for plate-type evidence
                        if (prev.photoType === PhotoType.UnobstructedPlate) {
                            setPlateInput(v.Plate || '');
                        }
                        return { ...prev, selectedVehicle: v };
                    })
                }
                timezone={pc.Timezone || appConfig.timeZone}
                prevGeoZones={prevGeoZones}
                geoZoneDisplayWidth={geoZoneDisplayWidth}
                plateInput={plateInput}
                onPlateInputChange={setPlateInput}
                loading={saving}
            />
        </Stack>
    );
});

export default LivePhotosTimeline;
